import type { DraftView, RuleGroupView } from '@tabel/shared/schemas/rules';
import type { PlanResponse } from '@tabel/shared/schemas/rules-plan';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, ChartColumn, CircleCheck, History, RefreshCw, Send, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { errorCode, errorText, useToast } from '../../components/ErrorToast';
import { Badge, Button, SidePanel, Skeleton } from '../../components/ui';
import { api, rulesKeys } from '../../lib/api';
import { ApiError, reportClientError } from '../../lib/errors';
import { ruDate, ruDateTime } from '../../lib/format';
import { useRole } from '../../lib/role';
import { loadPreview } from './ChangesView';
import { impactLine } from './ImpactLines';
import { shouldReport } from './LoadError';
import { PLAN_LABEL, PLAN_T, asSentence, sayPlan } from './plan-texts';
import { LABEL, say } from './texts';

/*
 * План изменения по ссылке (005, US5, T122; spec.md FR-035, FR-037, FR-038, FR-060; research R22;
 * contracts/http-api.md, `GET /api/rules/plans/:planId`). Агент замораживает план и даёт ссылку `/rules?plan=<номер>`;
 * страница «Правила расчёта» открывает правую панель плана — тот же `SidePanel`, что у «Расчёта дня»: поверх страницы у
 * правого края во всю высоту, 620 px; до 900 px — на весь экран.
 * - Полоса состояния под шапкой: «План действует до …»; устарел, устарел по сроку, опубликован — текст сервера
 *   (`stateText`: причина и что делать), у опубликованного — «Открыть в истории изменений».
 * - «Сведения о плане»: метка «Подготовлено ИИ-агентом», клиент агента, время заморозки, срок, номер, дата начала.
 * - Состав плана: что изменится, основание от агента, влияние при заморозке и сейчас (`same` — числа те же, `fresh` —
 *   новые числа рядом, `recompute` — «Посчитать влияние заново»: влияние с датой плана, затем план снова), примеры и
 *   риски к подтверждению с флажками на время заморозки, предупреждения проверки правил.
 * - Подвал: «Опубликовать по плану» — только ведущему черновика плана и только у действующего плана (`canPublish`
 *   сервера и черновик страницы, `mayPublishPlan`). Остальным — только чтение и почему. Публикует окно 004 с основанием
 *   из плана. Пока план открыт, полоса черновика страницы публикует тоже только по плану (итоговое ревью T160).
 * - Неверный номер в ссылке (`?plan=abc`) и плана нет (`404`) — «Такого плана нет» и «Закрыть» (итоговое ревью T160).
 * Часы панель не считает: числа влияния — с сервера.
 */

/** Полоса состояния под шапкой панели. */
const STRIP_TONE = {
  ok: 'bg-ok-soft text-ok',
  warn: 'bg-warn-soft text-warn',
  accent: 'bg-accent-soft text-accent',
} as const;

function StateStrip({
  tone,
  icon: Icon,
  children,
  action,
}: {
  tone: keyof typeof STRIP_TONE;
  icon: typeof Bot;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section
      aria-label={PLAN_LABEL.state}
      className={`flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-line px-6 py-2.5 text-[13px] max-sm:px-4 ${STRIP_TONE[tone]}`}
    >
      <p className="flex min-w-0 flex-1 basis-[240px] items-start gap-2 break-words">
        <Icon size={14} strokeWidth={1.75} className="mt-[3px] shrink-0" aria-hidden />
        <span className="min-w-0">{children}</span>
      </p>
      {action}
    </section>
  );
}

function Part({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section aria-label={label} className="space-y-1.5 text-[13px]">
      <h3 className="text-[13.5px] font-semibold text-ink">{label}</h3>
      {children}
    </section>
  );
}

/** Строка свойства: подпись слева, значение справа (дизайн-система, Drawer). */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div data-row className="flex gap-3">
      <dt className="w-[184px] shrink-0 text-ink-2 max-sm:w-[136px]">{label}</dt>
      <dd className="min-w-0 break-words text-ink tabular-nums">{children}</dd>
    </div>
  );
}

const HINT = 'text-[12.5px] text-ink-3';

/** Полоса состояния проекта — и в панели проекта на странице «Помощник» и в правой панели «Помощник» (006, T090). */
export function stateOf(plan: PlanResponse, tz: string, onOpenVersion: (id: number) => void) {
  switch (plan.state) {
    case 'active':
      return (
        <StateStrip tone="accent" icon={CircleCheck}>
          {sayPlan.active(ruDateTime(plan.expiresAt, tz))}
        </StateStrip>
      );
    case 'published': {
      const v = plan.publishedVersion;
      return (
        <StateStrip
          tone="ok"
          icon={CircleCheck}
          action={
            v && (
              <Button icon={History} onClick={() => onOpenVersion(v.id)}>
                {PLAN_LABEL.openVersion}
              </Button>
            )
          }
        >
          {asSentence(plan.stateText ?? PLAN_T.publishedFallback)}
        </StateStrip>
      );
    }
    default:
      return (
        <StateStrip tone="warn" icon={TriangleAlert}>
          {asSentence(plan.stateText ?? PLAN_T.staleFallback)}
        </StateStrip>
      );
  }
}

/**
 * Ошибка загрузки плана: нет плана (`404`, с кодом `NOT_FOUND` или без тела), нет прав (`403`), сбой — что случилось,
 * код ошибки и «Повторить» у сбоя. Нет плана и нет прав — повтор ничего не даст: только текст и «Закрыть» в подвале.
 */
function PlanError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const e = error instanceof ApiError ? error : null;
  const known =
    e?.code === 'NOT_FOUND' || e?.status === 404
      ? PLAN_T.notFound
      : e?.status === 403
        ? PLAN_T.forbidden
        : null;
  // Код ошибки (requestId) и, если текст — слова сервера, код ответа: «Код ошибки: r-1 · PLAN_STALE».
  const codes = [e?.requestId, errorCode(error)].filter(Boolean).join(' · ');
  const codeLine = codes && <p className="text-[12px] text-ink-3 select-all">{say.errorCode(codes)}</p>;
  if (known)
    return (
      <div role="alert" className="space-y-2 text-[13px]">
        <p className="font-medium text-ink">{known}</p>
        {codeLine}
      </div>
    );
  return (
    <div role="alert" className="space-y-2 text-[13px]">
      <p className="font-medium text-ink">{PLAN_LABEL.loadError}</p>
      <p className="text-ink-2">{asSentence(errorText(error, PLAN_LABEL.loadError))}</p>
      {codeLine}
      <Button onClick={onRetry}>{LABEL.retry}</Button>
    </div>
  );
}

/** Ссылка с неверным номером плана (`?plan=abc`): сервер не спрашиваем — такого плана нет. */
function NoSuchPlan() {
  return (
    <div role="alert" className="text-[13px]">
      <p className="font-medium text-ink">{PLAN_T.notFound}</p>
    </div>
  );
}

/**
 * План по ссылке — один запрос и один ключ кэша для панели плана и полосы черновика страницы: полоса знает, можно ли
 * опубликовать по плану. `null` — номера плана в ссылке нет или он неверный: запроса нет.
 */
export function usePlan(planId: number | null) {
  const { role } = useRole();
  return useQuery({
    queryKey: rulesKeys.plan(role, planId ?? 0),
    queryFn: async () => {
      if (planId === null) throw new Error('номера проекта нет');
      try {
        return await api.rulesPlan(planId);
      } catch (e) {
        if (shouldReport(e)) reportClientError('Rules', 'load plan', e);
        throw e;
      }
    },
    enabled: planId !== null,
  });
}

/**
 * Опубликовать по плану можно: план действует, сервер разрешил (`canPublish`), и на странице видно то же — черновик
 * плана открыт, его ведёт этот пользователь в роли администратора правил. Одно правило для панели и полосы черновика.
 */
export function mayPublishPlan(
  plan: PlanResponse | undefined,
  draft: DraftView | null,
  role: string,
): plan is PlanResponse {
  return (
    plan !== undefined &&
    plan.state === 'active' &&
    plan.canPublish &&
    role === 'rules_admin' &&
    draft?.isLeader === true &&
    draft.id === plan.draftId
  );
}

/** Состав проекта — и в правой панели «Помощник» (006, T090): та же панель проекта, без второго вида. */
export function PlanBody({
  plan,
  draft,
  groups,
  tzOffset,
  mayPublish,
}: {
  plan: PlanResponse;
  draft: DraftView | null;
  groups: readonly RuleGroupView[];
  tzOffset: string;
  mayPublish: boolean;
}) {
  const { role } = useRole();
  const qc = useQueryClient();
  const { showError } = useToast();
  const question = (group: string | undefined) => groups.find((g) => g.id === group)?.question;
  const now = plan.state === 'active' ? plan.impact.now : undefined;

  // «Посчитать влияние заново»: влияние черновика с датой плана (004), затем план снова — числа «Сейчас».
  const recompute = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('черновика нет');
      return loadPreview(draft.etag, plan.validFrom);
    },
    onError: (e) => {
      if (shouldReport(e)) reportClientError('Rules', 'plan recompute', e);
      showError(e, { screen: 'Rules', action: 'plan recompute', retry: () => recompute.mutate() });
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: rulesKeys.plan(role, plan.planId) }),
  });
  const failed = recompute.data?.status === 'failed';

  return (
    <>
      <section aria-label={PLAN_LABEL.facts} className="space-y-3 text-[13px]">
        <Badge tone="accent">
          <Bot size={12} strokeWidth={1.75} aria-hidden />
          {PLAN_LABEL.prepared}
        </Badge>
        <dl className="space-y-1.5">
          <Row label={PLAN_LABEL.client}>{plan.preparedBy.client}</Row>
          <Row label={PLAN_LABEL.frozenAt}>{ruDateTime(plan.preparedBy.frozenAt, tzOffset)}</Row>
          <Row label={PLAN_LABEL.expiresAt}>{ruDateTime(plan.expiresAt, tzOffset)}</Row>
          <Row label={PLAN_LABEL.number}>{plan.planId}</Row>
          <Row label={PLAN_LABEL.validFrom}>{ruDate(plan.validFrom)}</Row>
        </dl>
      </section>

      <Part label={PLAN_LABEL.changes}>
        <ul className="list-disc space-y-0.5 pl-5 break-words text-ink-2">
          {plan.changes.map((c) => (
            <li key={`${c.group}.${c.field}`}>{c.text}</li>
          ))}
        </ul>
      </Part>

      <Part label={PLAN_LABEL.basis}>
        <div className="space-y-0.5 break-words">
          <p>{say.basisKind(plan.basis.kind)}</p>
          {plan.basis.decided.trim() && <p>{say.decided(plan.basis.decided)}</p>}
          {plan.basis.questionCode && <p>{say.question([plan.basis.questionCode])}</p>}
        </div>
        {mayPublish && <p className={HINT}>{PLAN_T.basisEditable}</p>}
      </Part>

      <Part label={PLAN_LABEL.impact}>
        <div className="flex items-start gap-2.5 rounded-[10px] border border-dashed border-line-strong px-3 py-2.5">
          <ChartColumn size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-ink-3" aria-hidden />
          <div className="min-w-0 flex-1 space-y-1 break-words">
            <p>{impactLine(PLAN_LABEL.frozen, plan.impact.frozen.summary)}</p>
            {now?.status === 'fresh' && (
              <p className="font-medium text-ink">{impactLine(PLAN_LABEL.now, now.summary)}</p>
            )}
            {now?.status === 'same' && <p className="text-ink-2">{PLAN_T.sameData}</p>}
            {now?.status === 'fresh' && <p className="text-ink-2">{PLAN_T.freshData}</p>}
            {now?.status === 'recompute' && <p className="text-warn">{PLAN_T.recompute}</p>}
            {now?.status === 'recompute' && failed && <p className="text-bad">{PLAN_T.recomputeError}</p>}
          </div>
        </div>
        {now?.status === 'recompute' && draft && (
          <Button
            icon={RefreshCw}
            disabled={recompute.isPending}
            aria-busy={recompute.isPending || undefined}
            onClick={() => recompute.mutate()}
          >
            {recompute.isPending ? LABEL.computing : PLAN_LABEL.recompute}
          </Button>
        )}
      </Part>

      {plan.goldensToConfirm.length > 0 && (
        <Part label={PLAN_LABEL.goldens}>
          <ul className="space-y-1.5">
            {plan.goldensToConfirm.map((g) => (
              <li key={g.goldenId} className="rounded-[8px] border border-line px-3 py-2 break-words">
                <p className="text-ink">{g.title ?? g.code}</p>
                <p className={g.confirmed ? 'text-ok' : 'text-ink-2'}>
                  {g.confirmed ? PLAN_T.goldenConfirmed : PLAN_T.goldenToConfirm}
                </p>
              </li>
            ))}
          </ul>
          {mayPublish && <p className={HINT}>{PLAN_T.goldensHint}</p>}
        </Part>
      )}

      {plan.risksToAccept.length > 0 && (
        <Part label={PLAN_LABEL.risks}>
          <ul className="space-y-1.5">
            {plan.risksToAccept.map((r) => (
              <li key={r.code} className="rounded-[8px] bg-warn-soft px-3 py-2 break-words text-warn">
                <p className="flex items-start gap-2">
                  <TriangleAlert size={16} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
                  <span className="min-w-0">{r.text}</span>
                </p>
                <p className="pl-6 text-ink-2">{r.acked ? PLAN_T.riskAcked : PLAN_T.riskToAck}</p>
              </li>
            ))}
          </ul>
          {mayPublish && <p className={HINT}>{PLAN_T.risksHint}</p>}
        </Part>
      )}

      {plan.warnings.length > 0 && (
        <Part label={PLAN_LABEL.warnings}>
          <ul className="space-y-1.5">
            {plan.warnings.map((w) => (
              <li key={`${w.code}|${w.group ?? ''}`} className="break-words">
                <p className="text-ink">{w.text}</p>
                {question(w.group) && <p className="text-ink-3">{question(w.group)}</p>}
              </li>
            ))}
          </ul>
        </Part>
      )}
    </>
  );
}

export function PlanPanel({
  planId,
  open,
  draft,
  groups,
  tzOffset,
  onClose,
  onPublish,
  onOpenVersion,
}: {
  /** Номер плана из ссылки; `null` — номер в ссылке неверный (`?plan=abc`): «Такого плана нет». */
  planId: number | null;
  /** Панель видна; пока открыт «Расчёт дня», план ждёт под ним. */
  open: boolean;
  /** Черновик на странице: ведущий, его `etag` для «Посчитать влияние заново». */
  draft: DraftView | null;
  /** Правила страницы — вопрос правила у предупреждения. */
  groups: readonly RuleGroupView[];
  tzOffset: string;
  onClose: () => void;
  /** «Опубликовать по плану»: окно публикации 004 с основанием из плана. */
  onPublish: (plan: PlanResponse) => void;
  /** «Открыть в истории изменений» у опубликованного плана. */
  onOpenVersion: (versionId: number) => void;
}) {
  const { role } = useRole();
  const q = usePlan(planId);
  const plan = q.data;

  // Публикует ведущий черновика плана и только действующий план: так решил сервер (`canPublish`) и так видно на
  // странице (черновик тот же, его ведёт этот пользователь в роли администратора правил).
  const mayPublish = mayPublishPlan(plan, draft, role);
  // Только чтение у действующего плана — почему.
  const readOnly =
    plan?.state === 'active' && !mayPublish
      ? role === 'approver'
        ? PLAN_T.approverOnly
        : draft && !draft.isLeader
          ? sayPlan.leaderOnly(draft.leader?.name ?? null)
          : PLAN_T.leaderOnlyNoName
      : null;

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      label={PLAN_LABEL.panel}
      title={PLAN_LABEL.panel}
      icon={Bot}
      status={plan && stateOf(plan, tzOffset, onOpenVersion)}
      footer={
        <>
          {readOnly && (
            <p className="mr-auto min-w-0 flex-1 basis-[240px] text-[12.5px] text-ink-2">{readOnly}</p>
          )}
          <Button className={readOnly ? 'max-sm:flex-1' : 'ml-auto max-sm:flex-1'} onClick={onClose}>
            {PLAN_LABEL.close}
          </Button>
          {plan && mayPublish && (
            <Button variant="primary" icon={Send} className="max-sm:flex-1" onClick={() => onPublish(plan)}>
              {PLAN_LABEL.publish}
            </Button>
          )}
        </>
      }
    >
      {planId === null && <NoSuchPlan />}
      {q.isLoading && (
        <div aria-busy="true" aria-label={LABEL.loading} className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}
      {q.error && !plan && <PlanError error={q.error} onRetry={() => void q.refetch()} />}
      {plan && (
        <PlanBody plan={plan} draft={draft} groups={groups} tzOffset={tzOffset} mayPublish={mayPublish} />
      )}
    </SidePanel>
  );
}
